import React, { useEffect, useMemo, useState } from 'react';
import { Badge, Button, Card, CardBody, CardHeader, Col, Form, Row, Table } from '@edgebox/react-components';
import { useNavigate } from 'react-router-dom';
import { IPagedListResponse } from '@edgebox/data-definition-kit';
import { AccessScope, AiModelStatus } from '@edgebox/sync-core-data-definitions';
import { ClientAiModelEntity } from '@edgebox/sync-core-rest-client';
import { AccessScopeFilter, enumValues, normalizeFilterId, StatusFilter, useSyncCoreApi } from '../aiHelpers';
import { AiProviderName } from '../components/AiProviderName';
import { ErrorCard } from '../components/ErrorCard';
import { LoadingCard } from '../components/LoadingCard';
import { ScreenHeader } from '../components/ScreenHeader';

export function AiModelsView() {
  const api = useSyncCoreApi();
  const navigate = useNavigate();

  const [statusFilter, setStatusFilter] = useState<StatusFilter<AiModelStatus>>('Default');
  const [accessScope, setAccessScope] = useState<AccessScopeFilter>('Any');
  const [providerId, setProviderId] = useState<string>('');
  const [customerId, setCustomerId] = useState<string>('');
  const [projectId, setProjectId] = useState<string>('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(undefined);
  const [response, setResponse] = useState<IPagedListResponse<ClientAiModelEntity> | undefined>(undefined);

  const filters = useMemo(() => {
    return {
      status: statusFilter === 'Default' ? [AiModelStatus.Active] : statusFilter,
      accessScope: accessScope === 'Any' ? undefined : accessScope,
      providerId: normalizeFilterId(providerId),
      customerId: normalizeFilterId(customerId),
      projectId: normalizeFilterId(projectId),
    };
  }, [statusFilter, accessScope, providerId, customerId, projectId]);

  useEffect(() => {
    if (!api) return;
    let cancelled = false;
    setLoading(true);
    setError(undefined);

    api.ai.models
      .list({ page: 0, itemsPerPage: 50 }, filters)
      .then((res) => {
        if (cancelled) return;
        setResponse(res);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e);
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [api, filters]);

  const items = response?.items ?? [];

  return (
    <>
      <ScreenHeader
        title="Models"
        right={
          <Button variant="primary" onClick={() => navigate('/ai/model/new')}>
            Add model
          </Button>
        }
      />

      <Card className="mb-3">
        <CardBody>
          <Form>
            <Row className="g-3">
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    value={statusFilter === 'Default' ? 'Default' : 'Custom'}
                    onChange={(e) => setStatusFilter(e.target.value === 'Default' ? 'Default' : [])}
                  >
                    <option value="Default">Default (Active)</option>
                    <option value="Custom">Custom</option>
                  </Form.Select>
                </Form.Group>

                {statusFilter !== 'Default' && (
                  <div className="mt-2">
                    {enumValues(AiModelStatus).map((s) => (
                      <Form.Check
                        key={s}
                        type="checkbox"
                        label={s}
                        checked={(statusFilter as AiModelStatus[]).includes(s)}
                        onChange={(e) => {
                          const current = statusFilter as AiModelStatus[];
                          setStatusFilter(e.target.checked ? [...current, s] : current.filter((x) => x !== s));
                        }}
                      />
                    ))}
                  </div>
                )}
              </Col>

              <Col md={3}>
                <Form.Group>
                  <Form.Label>Access scope</Form.Label>
                  <Form.Select value={accessScope} onChange={(e) => setAccessScope(e.target.value as AccessScopeFilter)}>
                    <option value="Any">Any</option>
                    {enumValues(AccessScope).map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={2}>
                <Form.Group>
                  <Form.Label>Provider ID</Form.Label>
                  <Form.Control value={providerId} onChange={(e) => setProviderId(e.target.value)} placeholder="Optional" />
                </Form.Group>
              </Col>

              <Col md={2}>
                <Form.Group>
                  <Form.Label>Customer ID</Form.Label>
                  <Form.Control value={customerId} onChange={(e) => setCustomerId(e.target.value)} placeholder="Optional" />
                </Form.Group>
              </Col>

              <Col md={2}>
                <Form.Group>
                  <Form.Label>Project ID</Form.Label>
                  <Form.Control value={projectId} onChange={(e) => setProjectId(e.target.value)} placeholder="Optional" />
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </CardBody>
      </Card>

      {loading ? <LoadingCard text="Loading models…" /> : null}
      {error ? <ErrorCard error={error} /> : null}

      <Card>
        <CardHeader>
          Models <Badge bg="secondary">{response?.totalNumberOfItems ?? items.length}</Badge>
        </CardHeader>
        <CardBody>
          <Table striped bordered hover size="sm" responsive className="mb-0">
            <thead>
              <tr>
                <th>Name</th>
                <th>Status</th>
                <th>Scope</th>
                <th>Provider</th>
                <th>Model key</th>
                <th>Frozen</th>
                <th style={{ width: '1%' }}></th>
              </tr>
            </thead>
            <tbody>
              {items.map((m) => (
                <tr key={m.id}>
                  <td>{m.name}</td>
                  <td>
                    <Badge bg={m.status === AiModelStatus.Active ? 'success' : 'secondary'}>{m.status}</Badge>
                  </td>
                  <td>{m.accessScope}</td>
                  <td>
                    <AiProviderName id={m.provider?.getId?.()} />
                  </td>
                  <td className="text-monospace">{m.modelKey ?? '-'}</td>
                  <td>{m.frozen ? 'Yes' : 'No'}</td>
                  <td>
                    <Button size="sm" variant="light" onClick={() => navigate(`/ai/model/${m.id}`)}>
                      Details
                    </Button>
                  </td>
                </tr>
              ))}
              {!items.length && (
                <tr>
                  <td colSpan={7} className="text-muted">
                    No models found.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </CardBody>
      </Card>
    </>
  );
}
